import { PortalProvider } from "@gorhom/portal";
import { useFonts } from "expo-font";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Platform, ScrollView } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { Navigate, Route, Routes } from "react-router";
import { Aurora, PortalProvider as AuroraPortalProvider } from "@lookiero/aurora";
import { setPaymentsBridge } from "@lookiero/payments-front";
import { PromoCodesProvider, setPromocodesBridge } from "@lookiero/promocodes-front";
import { Locale } from "@lookiero/sty-psp-locale";
import { SentryEnvironment } from "@lookiero/sty-psp-logging/sentry";
import { Text } from "@lookiero/sty-psp-ui";
import { DummyLayout, themeByTradename } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { bootstrap as lookInBootstrap } from "./infrastructure/delivery/bootstrap";
import { bootstrap as lookInMockBootstrap } from "./infrastructure/delivery/bootstrap.mock";
import { root } from "./infrastructure/ui/Root";
import { PaymentsContextProvider } from "./infrastructure/ui/hooks/usePaymentsContext";
import { Router } from "./infrastructure/ui/routing/router/Router";
import { Session } from "./infrastructure/ui/shared/types";
import { VERSION } from "./version";

const apiUrl =
  Platform.OS !== "web"
    ? "https://web2.sp.dev.aws.lookiero.es/look-in/api"
    : __DEV__
      ? process.env.EXPO_PUBLIC_CI
        ? "/local-to-dev"
        : "http://localhost:3005/local-to-dev"
      : "/look-in/api";

const sentryConfig: SentryEnvironment = {
  publicKey: "493de0c99054c688c660fc2af620954a",
  project: "4509078345220096",
  release: VERSION,
  environment: "development",
  dist: "web-standalone",
};

const hostUrl = "http://localhost:19006/shop/checkout";

const useRedirect: () => Record<string, string> = () => ({
  returnUrl: hostUrl,
});

interface ExpoRootProps {
  readonly session: Session;
  readonly tradename: Tradename;
  readonly locale?: Locale;
  readonly getAuthToken: () => Promise<string>;
}
const ExpoRoot: FC<ExpoRootProps> = ({ session, tradename, locale = Locale.es_ES, getAuthToken }) => {
  const [fontsLoaded] = useFonts({
    ["AreaInktrap-Semibold"]: require("@lookiero/aurora/build/assets/fonts/AreaInktrap-Semibold.otf"),
    ["AreaNormal-Semibold"]: require("@lookiero/aurora/build/assets/fonts/AreaNormal-Semibold.otf"),
    ["AreaNormal-Extrabold"]: require("@lookiero/aurora/build/assets/fonts/AreaNormal-Extrabold.otf"),
    ["OpenSans-Regular"]: require("@lookiero/aurora/build/assets/fonts/OpenSans-Regular.ttf"),
    ["OpenSans-Bold"]: require("@lookiero/aurora/build/assets/fonts/OpenSans-Bold.ttf"),
    auroraicons: require("@lookiero/aurora-iconfont/dist/auroraicons.ttf"),
  });

  const theme = useMemo(() => themeByTradename({ tradename }), [tradename]);

  useEffect(() => {
    const noop = () => void 0;
    setPaymentsBridge({
      locale: () => locale,
      getToken: getAuthToken,
      stripeKey: "pk_test_6dwdUfkUm7AaUH3oSje9H0kk",
      stripeUkKey:
        "pk_test_51Jw6NEHQ1gSUXaQ8KKBLBhmzw7k0TCTN0GQ6qq8oGRmXCn09ptn7zrHX5jsyidv8iBDGuzUDV3R0fko01m5oJzMO00z6XmoLbn",
      commonAssetsPath: "https://cdn.dev.envs.lookiero.tech/commons",
      paymentsAssetsPath: "https://cdn.dev.envs.lookiero.tech/payments-front",
      useAvoidKeyboard: () => ({
        // @ts-ignore
        setAvoidKeyboard: noop,
      }),
      emitTrackingEvent: (event) => {
        console.log("Tracking Payment event", event);
      },
      appVersion: VERSION,
      graphqlUri: "",
      useFeatureFlags: () => ({ ["DISABLE_KLARNA"]: false, ["USE_LARGE_TTL_IN_PAYMENTS_FRONT_CACHE"]: false }),
      scrollView: ScrollView,
      hostUrl,
      tradename,
    });

    setPromocodesBridge({
      getToken: getAuthToken,
      useFeatureFlags: () => ({}),
      appVersion: "0.0.0-local",
      useAvoidKeyboard: () => ({
        // @ts-ignore
        setAvoidKeyboard: noop,
      }),
      emitTrackingEvent: (event) => {
        console.log("Tracking Promocodes event", event);
      },
      proxy: (url) => url,
    });
  }, [getAuthToken, locale, tradename]);

  const { Component: Messaging } = useMemo(
    () =>
      process.env.EXPO_PUBLIC_APP_VARIANT === "test"
        ? lookInMockBootstrap()
        : lookInBootstrap({ apiUrl: () => apiUrl, getAuthToken }),
    [getAuthToken],
  );

  const Root = useMemo(
    () => root({ Messaging, development: false, sentry: () => sentryConfig })({ customerId: session.user.id }),
    [Messaging, session.user.id],
  );

  const [isAccessible, setIsAccessible] = useState<boolean>();
  const onNotAccessible = useCallback(() => setIsAccessible(false), []);

  return fontsLoaded ? (
    <SafeAreaProvider>
      <PortalProvider>
        <PaymentsContextProvider>
          <Aurora theme={theme}>
            {isAccessible === false && <Text variant="heading">LookIn is not accessible!</Text>}

            <AuroraPortalProvider>
              <PromoCodesProvider>
                <Router>
                  <Routes>
                    <Route
                      path="/shop/*"
                      element={
                        <Root
                          apiUrl={() => apiUrl}
                          basePath="/shop"
                          cdnPath={`https://cdn.dev.envs.lookiero.tech/${tradename.toLowerCase()}`}
                          layout={DummyLayout}
                          locale={locale}
                          rafComponent={() => null}
                          session={session}
                          tradename={tradename}
                          useRedirect={useRedirect}
                          onBack={() => void 0}
                          onBackToStyleProfile={() => void 0}
                          onNotAccessible={onNotAccessible}
                          onPressLookAndLikeBanner={() => void 0}
                          onShowReturnInstructions={() => void 0}
                        />
                      }
                    />

                    <Route element={<Navigate to="/shop" replace />} path="*" />
                  </Routes>
                </Router>
              </PromoCodesProvider>
            </AuroraPortalProvider>
          </Aurora>
        </PaymentsContextProvider>
      </PortalProvider>
    </SafeAreaProvider>
  ) : null;
};

export { ExpoRoot };
